<template>
  <div class="-mt-[64px]">
    <!-- Hero Section -->
    <section class="relative h-screen overflow-hidden z-0">
      <!-- Background Video -->
      <video
        ref="backgroundVideo"
        class="absolute inset-0 w-full h-full object-cover z-0"
        :src="videoSource"
        autoplay
        muted
        loop
        playsinline
      ></video>
      <!-- Overlay -->
      <div class="absolute inset-0 background-gradient margin-b-[-2px]"></div>
      <!-- Content -->
      <div
        class="container absolute inset-0 flex flex-col justify-center items-center text-white text-center px-6"
      >
        <!-- Logo -->
        <router-link to="/"
          ><img
            v-motion
            :initial="{ opacity: 0 }"
            :visibleOnce="{ opacity: 1 }"
            :delay="200"
            :duration="400"
            alt="Coast to Coast Adventures logo"
            src="@/assets/logos/logoFilled.png"
            class="h-32 mb-4"
        /></router-link>

        <!-- 1st heading -->
        <h1
          v-motion
          :initial="{ opacity: 0, y: -24 }"
          :visibleOnce="{ opacity: 1, y: 0 }"
          :delay="200"
          :duration="400"
          class="text-3xl md:text-6xl font-black mb-10 banner-text"
        >
          Discover <span class="italic">Costa Rica</span>
        </h1>

        <!-- 2nd heading -->
        <!-- <h1
          v-motion
          :initial="{ opacity: 0, y: -24 }"
          :visibleOnce="{ opacity: 1, y: 0 }"
          :delay="800"
          :duration="800"
          class="text-3xl md:text-6xl font-black mb-10 banner-text"
        >
          Your
          <span class="italic">Coast to Coast</span>
          Adventure Awaits
        </h1> -->

        <!-- Buttons -->
        <div class="flex flex-col items-center space-y-4 w-[240px]">
          <router-link
            ref="seeAdventuresRef"
            to="/all-trips"
            v-motion="controls"
            @mouseenter="onHover('seeAdventuresRef')"
            @mouseleave="onUnhover('seeAdventuresRef')"
            class="banner-button btn font-medium block bg-white text-black hover:bg-black hover:text-white hover:scale-125 py-2 px-4 rounded-lg shadow-lg text-xl transition w-full"
          >
            <span class="material-symbols-outlined align-middle rotate-90">
              explore
            </span>
            View Adventures
          </router-link>
          <router-link
            ref="createOwnRef"
            to="/custom-trips"
            v-motion="controls"
            @mouseenter="onHover('createOwnRef')"
            @mouseleave="onUnhover('createOwnRef')"
            class="banner-button banner-text btn font-medium block bg-transparent text-white border border-white hover:border-black hover:bg-black hover:text-white hover:scale-125 py-2 px-4 rounded-lg shadow-lg text-xl transition w-full"
          >
            <span class="material-symbols-outlined align-middle">
              add_location_alt
            </span>
            Build Custom Trip
          </router-link>
        </div>

        <!-- Chevron pointing down -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 50 }"
          :visibleOnce="{ opacity: 1, y: 0 }"
          :delay="1800"
          :duration="800"
          class="absolute bottom-24 sm:bottom-12 animate-bounce"
        >
          <span class="material-symbols-outlined align-middle text-3xl">
            keyboard_arrow_down
          </span>
        </div>
      </div>
    </section>

    <!-- Adventure Section -->
    <div class="bg-stone-800 text-white text-center py-4 shadow-md">
      <div class="container">
        <h2
          v-motion
          :initial="{ opacity: 0, scale: 0.6 }"
          :visibleOnce="{ opacity: 1, scale: 1 }"
          :delay="200"
          :duration="400"
          class="text-2xl lg:text-4xl font-bold"
        >
          What kind of an adventure are you looking for?
        </h2>
      </div>
    </div>
    <section class="flex items-center adventure-section-background">
      <div class="container py-12 text-left lg:text-xl xl:text-2xl">
        <p
          v-motion
          :initial="{ opacity: 0, x: -100 }"
          :visibleOnce="{ opacity: 1, x: 0, scale: 1 }"
          :delay="200"
          :duration="400"
        >
          Picture yourself gliding above a rainforest canopy at dawn, the mist
          gently lifting to reveal a sea of emerald green.
          <span class="font-semibold"
            >And that’s only the beginning of your day in Costa Rica.</span
          >
        </p>
        <p
          class="mt-4"
          v-motion
          :initial="{ opacity: 0, x: -100 }"
          :visibleOnce="{ opacity: 1, x: 0, scale: 1 }"
          :delay="200"
          :duration="400"
        >
          With over 30 years of experience, Coast to Coast Adventures handles
          every detail of your journey—so you can dive into the excitement of
          hiking, rafting, and exploring, without worrying about the logistics.
        </p>
        <p
          class="mt-4"
          v-motion
          :initial="{ opacity: 0, x: -100 }"
          :visibleOnce="{ opacity: 1, x: 0, scale: 1 }"
          :delay="200"
          :duration="400"
        >
          Whether you’re a solo explorer, a family seeking quality time, a
          student group chasing adrenaline, a charity team building bonds, or a
          corporate retreat wanting fresh inspiration, our wide range of
          activities scales to your group’s size and style.
        </p>
        <p
          class="mt-4"
          v-motion
          :initial="{ opacity: 0, x: -100 }"
          :visibleOnce="{ opacity: 1, x: 0, scale: 1 }"
          :delay="200"
          :duration="400"
        >
          Join the thousands of travelers, schools, nonprofits, and companies
          who’ve trusted Coast to Coast Adventures to turn Costa Rica’s raw
          beauty into unforgettable experiences.
        </p>
        <p
          class="mt-4 mb-4 font-medium text-center text-xl lg:text-2xl xl:text-3xl"
          v-motion
          :initial="{ opacity: 0, x: -100 }"
          :visibleOnce="{ opacity: 1, x: 0, scale: 1 }"
          :delay="200"
          :duration="400"
        >
          Ready to craft your next great story?
        </p>

        <ImageGrid :images="visibleAdventureImages" />
      </div>
    </section>

    <!-- Deals Section -->
    <section class="deals-background pb-8">
      <div class="container text-center py-4">
        <h2
          class="text-2xl lg:text-4xl font-bold"
          v-motion
          :initial="{ opacity: 0, scale: 0.6 }"
          :visibleOnce="{ opacity: 1, scale: 1 }"
          :delay="200"
          :duration="500"
        >
          Exclusive group offers
        </h2>
      </div>

      <div v-if="isLoadingDeals" class="text-center py-10 text-gray-600">
        Loading trip deals...
      </div>
      <div v-else-if="dealsError" class="text-center py-10 text-red-600">
        {{ dealsError }}
      </div>
      <div v-else class="container">
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center"
        >
          <TripCard
            v-motion
            :initial="{ opacity: 0, y: 30 }"
            :visibleOnce="{ opacity: 1, y: 0 }"
            :delay="200"
            :duration="500"
            class="w-full max-w-sm pb-4"
            v-for="deal in tripDeals"
            :key="deal.name"
            :promotionText="deal.overview.promotionText"
            :code="deal.code"
            :name="deal.name"
            :map="deal.overview.mapSrc"
            :photos="deal.photos"
            :length="deal.overview.length"
            :price="deal.overview.priceSection?.primary?.price || ''"
            :sellText="deal.overview.sellText"
          />
        </div>
      </div>
    </section>

    <!-- Reviews Section -->
    <section class="py-4 bg-stone-200">
      <div class="text-center mb-8 container">
        <h2
          class="text-2xl lg:text-4xl font-bold mb-2"
          v-motion
          :initial="{ opacity: 0, scale: 0.6 }"
          :visibleOnce="{ opacity: 1, scale: 1 }"
          :delay="200"
          :duration="500"
        >
          What Our Adventurers Say
        </h2>
        <p
          class="text-gray-600"
          v-motion
          :initial="{ opacity: 0, y: 20 }"
          :visibleOnce="{ opacity: 1, y: 0 }"
          :delay="200"
          :duration="500"
        >
          Real experiences from our satisfied customers
        </p>
      </div>

      <div
        class="container flex flex-col lg:flex-row gap-4 justify-center items-start mb-4"
      >
        <div
          class="max-w-md mx-auto bg-white rounded-lg shadow-md p-6"
          v-motion
          :initial="{ opacity: 0, y: 40 }"
          :visibleOnce="{ opacity: 1, y: 0 }"
          :delay="200"
          :duration="500"
        >
          <div class="flex items-center mb-4">
            <img
              src="@/assets/reviews/julieV.jpg"
              alt="Emily Johnson"
              class="w-12 h-12 rounded-full object-cover mr-4"
            />
            <div>
              <h4 class="font-semibold text-left">Patrick M</h4>
              <div class="flex text-yellow-400">
                <span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate
                </span>
                <span class="material-symbols-outlined align-middle">
                  star_rate
                </span>
              </div>
            </div>
          </div>
          <p class="text-gray-600 italic">
            "Awesome experience in CR thanks to Coast to Coast! Randall helped
            us plan an amazing trip with a ton of fun, engaging activities.
            Carlos made sure we made it from one destination to the next, always
            sharing great insights and helping us to learn more about his
            country. We can't say enough about how great Coast to Coast was, I'd
            recommend them to anyone looking for a fun, active trip to CR!"
          </p>
        </div>
        <div
          class="max-w-md mx-auto bg-white rounded-lg shadow-md p-6"
          v-motion
          :initial="{ opacity: 0, y: 40 }"
          :visibleOnce="{ opacity: 1, y: 0 }"
          :delay="200"
          :duration="500"
        >
          <div class="flex items-center mb-4">
            <img
              src="@/assets/reviews/terryS.jpg"
              alt="Michael Rodriguez"
              class="w-12 h-12 rounded-full object-cover mr-4"
            />
            <div>
              <h4 class="font-semibold text-left">Terry S</h4>
              <div class="flex text-yellow-400">
                <span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate
                </span>
                <span class="material-symbols-outlined align-middle">
                  star_rate
                </span>
              </div>
            </div>
          </div>
          <p class="text-gray-600 italic">
            "Coast to Coast Adventures was great organizing a custom "Best of
            Camino de Costa Rica" 12 day hike for 13 friends. The trip was
            executed perfectly with our outstanding guides and transport van.
            Pre-trip, I was a bit concerned about the "camping". The ambiance of
            the camp grounds and comfort of the platform tents, made for great
            sleeping and a highlight of this adventure. If you are going to
            Costa Rica, this is the company you want use."
          </p>
        </div>
        <div
          class="max-w-md mx-auto bg-white rounded-lg shadow-md p-6"
          v-motion
          :initial="{ opacity: 0, y: 40 }"
          :visibleOnce="{ opacity: 1, y: 0 }"
          :delay="200"
          :duration="500"
        >
          <div class="flex items-center mb-4">
            <img
              src="@/assets/reviews/debDevon.jpg"
              alt="Michael Rodriguez"
              class="w-12 h-12 rounded-full object-cover mr-4"
            />
            <div>
              <h4 class="font-semibold text-left">Deb Devon</h4>
              <div class="flex text-yellow-400">
                <span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate </span
                ><span class="material-symbols-outlined align-middle">
                  star_rate
                </span>
                <span class="material-symbols-outlined align-middle">
                  star_rate
                </span>
              </div>
            </div>
          </div>
          <p class="text-gray-600 italic">
            "For those with adventurous spirit, the Costa Rica Coast to Coast is
            not to be missed. Our guide and support crew were fantastic enabling
            us to travel under our own steam, hiking, biking, rafting and
            kayaking across and through the Costa Rica rain forests seeing and
            experiencing all it has to offer. One word.....amazing!"
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import TripCard from "@/components/TripCard.vue";
import heroVideo from "@/assets/hero/hero.mp4";
import ImageGrid from "@/components/ImageGrid.vue";
import { useMotionProperties, useMotionControls } from "@vueuse/motion";
import { fetchTripsData } from "@/services/tripsService";

// Deals
const tripDeals = ref([]);
const isLoadingDeals = ref(true);
const dealsError = ref(null);

// Background video source
const videoSource = ref(heroVideo);
const backgroundVideo = ref(null);
const seeAdventuresRef = ref(null);
const createOwnRef = ref(null);
const seeAdventureControls = useMotionControls(
  useMotionProperties(seeAdventuresRef).motionProperties
);
const createOwnControls = useMotionControls(
  useMotionProperties(createOwnRef).motionProperties
);

const loadTripDeals = async () => {
  isLoadingDeals.value = true;
  dealsError.value = null;
  try {
    const data = await fetchTripsData();
    tripDeals.value = data.tripsWithPromotions ?? [];
  } catch (error) {
    dealsError.value = "Unable to load trip deals right now.";
  } finally {
    isLoadingDeals.value = false;
  }
};

onMounted(async () => {
  loadTripDeals();
  await nextTick();

  seeAdventureControls.set({
    opacity: 0,
    x: -60,
    scale: 1,
  });
  seeAdventureControls.apply({
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      delay: 400,
      duration: 600,
    },
  });

  createOwnControls.set({
    opacity: 0,
    x: 60,
    scale: 1,
  });
  createOwnControls.apply({
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      delay: 400,
      duration: 600,
    },
  });
});

function onHover(key) {
  if (key === "seeAdventuresRef") {
    seeAdventureControls.apply({
      scale: 1.3,
      transition: { delay: 100, duration: 200 },
    });
  }
  if (key === "createOwnRef") {
    createOwnControls.apply({
      scale: 1.3,
      transition: { delay: 100, duration: 200 },
    });
  }
}

function onUnhover(key) {
  if (key === "seeAdventuresRef") {
    seeAdventureControls.apply({
      scale: 1,
      transition: { delay: 100, duration: 200 },
    });
  }
  if (key === "createOwnRef") {
    createOwnControls.apply({
      scale: 1,
      transition: { delay: 100, duration: 200 },
    });
  }
}
</script>

<style scoped>
.parallax-image {
  display: none;
}
.banner-text {
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.87);
}
.banner-button {
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6);
}
.background-gradient {
  background: var(--gradient-transparent);
}

.adventure-section-background {
  background: var(--gradient-background-landing-info);
}

.deals-background {
  background: var(--gradient-background);
}
</style>
